import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadAsset } from '@/api/assets';
import { uploadEmbroideryImage } from '@/api/embroidery';
import { logger } from '@/utils/logger';
import { loadImage } from '@/utils/mockupLayers';
import { proxyStorageUrl } from '@/lib/product-transformer';
import {
  useGuidedEntryStore,
  type GuidedSide,
} from '@/stores/guidedEntryStore';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import { rejectMotif, motifRejectionMessage } from './guidedUpload';
import type { GuidedEmbroideryLimits } from './GuidedMotifCard';

/**
 * Notes any change of run while an upload is under way. Comparing the
 * procedure after the await misses a switch there and back, and the cards
 * are not reliably remounted either: AnimatePresence brings back a card that
 * is still fading out.
 */
function watchRunSwitch(): { switched: () => boolean; stop: () => void } {
  let switched = false;
  const stop = useGuidedEntryStore.subscribe((state, previous) => {
    if (state.procedure !== previous.procedure) switched = true;
  });
  return { switched: () => switched, stop };
}

/**
 * The two upload paths of a guided card: a print motif goes to the assets
 * API, an embroidery upload is analysed on the server and opens the thread
 * modal. Both drop their result when the run changed meanwhile.
 */
export function useGuidedUpload({
  side,
  embroidery,
}: {
  side: GuidedSide;
  embroidery?: GuidedEmbroideryLimits;
}) {
  const { t } = useTranslation();
  const setMotif = useGuidedEntryStore((s) => s.setMotif);
  const setEmbroideryPending = useGuidedEntryStore(
    (s) => s.setEmbroideryPending
  );

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEmbroideryRun = embroidery !== undefined;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;

    const rejection = rejectMotif(file, { embroidery: isEmbroideryRun });
    if (rejection) {
      setError(motifRejectionMessage(rejection));
      return;
    }

    setError(null);
    setIsUploading(true);
    try {
      if (isEmbroideryRun) {
        await uploadForEmbroidery(file);
        return;
      }
      const runSwitch = watchRunSwitch();
      let asset: Awaited<ReturnType<typeof uploadAsset>>;
      try {
        asset = await uploadAsset(file);
      } finally {
        runSwitch.stop();
      }
      // The seller may have switched to the embroidery run meanwhile, or
      // there and back: either way the upload belongs to a run that was
      // emptied, and a print motif must not land on what came after.
      if (runSwitch.switched()) return;
      if (!asset.width || !asset.height) {
        // Without natural dimensions a preset cannot be fitted and designRatio
        // would be a guess — better to refuse than to place something wrong.
        setError(t('creator.guidedMotif.dimensionsUnreadable'));
        return;
      }
      setMotif(side, {
        assetId: asset.id,
        url: asset.url,
        width: asset.width,
        height: asset.height,
        fileName: asset.name || file.name,
      });
    } catch (err) {
      logger.warn('GuidedMotifCard', 'Upload failed', err);
      setError(t('creator.guidedMotif.uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Embroidery upload: the server analyses the colours and keeps the source.
   * The blob URL stays for the render — reading pixels back from the
   * posterized preview would map already-quantized colours.
   */
  const uploadForEmbroidery = async (file: File) => {
    const sourceUrl = URL.createObjectURL(file);
    const runSwitch = watchRunSwitch();
    try {
      const [result, image] = await Promise.all([
        uploadEmbroideryImage(file),
        loadImage(sourceUrl),
      ]).finally(runSwitch.stop);
      if (!(image.naturalWidth > 0) || !(image.naturalHeight > 0)) {
        URL.revokeObjectURL(sourceUrl);
        setError(t('creator.guidedMotif.dimensionsUnreadable'));
        return;
      }
      // The round trip took a while; the seller may have switched to the
      // print run meanwhile. Landing the analysis there would put an
      // embroidery motif — and its modal — onto a print card.
      if (runSwitch.switched()) {
        URL.revokeObjectURL(sourceUrl);
        return;
      }
      const previewUrl =
        proxyStorageUrl(result.previewUrl) ?? result.previewUrl;
      setEmbroideryPending(side, {
        embFileKey: result.embFileKey,
        previewUrl,
        sourceUrl,
        palette: result.palette,
        width: image.naturalWidth,
        height: image.naturalHeight,
        fileName: result.originalName || file.name,
      });
      // The ceiling has to be in the store before the suggestion is narrowed
      // against it — the controls' effect that also sets it runs only after
      // this render. The modal opens right after the analysis, as in the
      // source design.
      const threads = useEmbroideryStore.getState();
      if (embroidery) {
        threads.setMaxColors(embroidery.maxColors);
        threads.setMinDpi(embroidery.minDpi);
      }
      threads.startMapping({
        palette: result.palette,
        objectUrl: sourceUrl,
        embFileKey: result.embFileKey,
        openModal: true,
      });
    } catch (err) {
      URL.revokeObjectURL(sourceUrl);
      throw err;
    }
  };

  return { isUploading, error, setError, handleFile };
}
